import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {
    CourseRoster, GradePostStatus,
    Roster,
    Student,
    StudentAddComment,
    StudentGradeValidationError,
    StudentList
} from './interface/roster.interface';
import {CompareValueService} from './compare-value.service';
import {UserComment} from './interface/comment.interface';
import {GradeList} from './interface/grading-rule.interface';
import {GradingRuleService} from './grading-rule.service';
import {DatePipe} from '@angular/common';
import {Subscription} from 'rxjs';

@Injectable()
export class RosterService {
    constructor(
        private http: HttpClient,
        private compareValueService: CompareValueService,
        private gradingRuleService: GradingRuleService,
        public datePipe: DatePipe
    ) {
    }

    currentRoster: CourseRoster = new (class implements CourseRoster {
        course: any;
        courseId: number;
        gradeSubmitNetId: string;
        id: number;
        roster: Array<Student>;
        rosterType: string;
        maxNumberGradeChange: number;
        numberOfGradeSubmit: number;
        displayName: string;
        firstName: string;
        lastName: string;
    })();

    //sort roster
    sortCourseRoster(roster: CourseRoster) {
        const compareValueService = this.compareValueService;
        roster.roster.sort(function (a: Student, b) {
            const aName = a.sortName.toUpperCase();
            const bName = b.sortName.toUpperCase();

            return compareValueService.sortValue(aName, bName);
        });
    }

    setCurrentRoster(roster: Roster) {
        if (roster.list.length > 0) {
            this.currentRoster = roster.list[0];
            this.sortCourseRoster(this.currentRoster);
        }
    }

    get(termCode: string, crn: string = '', ignoreCache: boolean = false): Observable<Roster> {
        return this.http.get<Roster>('/api/roster/' + termCode + '/' + crn + '/' + ignoreCache, {});
    }

    update(id: number, roster: Student[]): Observable<Roster> {
        return this.http.put<Roster>('/api/courseRoster/' + id, {roster: roster});
    }

    post(roster: CourseRoster, transactionId: string = null): Observable<StudentGradeValidationError> {
        return this.http.post<StudentGradeValidationError>('/api/courseRoster/', {
            roster: roster,
            transactionId: transactionId
        });
    }

    addOneStudent(roster: CourseRoster, student: Partial<Student>): Observable<StudentList> {
        return this.http.post<StudentList>('/api/courseStudent/' + roster.id, {student: student});
    }

    instructorAddStudentList(roster: CourseRoster): Observable<StudentList> {
        return this.http.get<StudentList>('/courseStudent/instructorAddStudentList/' + roster.id, {});
    }

    updateOneStudent(student: Partial<Student>): Observable<StudentList> {
        return this.http.put<StudentList>('/api/courseStudent/' + student.id, {student: student});
    }

    deleteOneStudent(student: Partial<Student>): Observable<StudentList> {
        return this.http.delete<StudentList>('/api/courseStudent/' + student.id, {});
    }

    postOneStudentComment(student: Student, comment: UserComment): Observable<StudentAddComment> {
        return this.http.post<StudentAddComment>('/courseStudent/addComment', {student: student, comment: comment});
    }

    addOneStudentComment(student: Student, canDelete: boolean = true): Observable<StudentAddComment> {
        const userComment: UserComment = {
            id: null,
            comment: student.tempComment,
            isDeleted: false,
            canDelete: canDelete,
            dateCreated: this.datePipe.transform(new Date(), 'MM/dd/yyyy hh:mm:ss a'),
            createdByNetId: null,
            createdByDisplayName: null,
            createdByFirstName: null,
            createdByLastName: null,
            lastUpdatedByNetId: null,
            lastUpdatedByDisplayName: null,
            lastUpdatedByFirstName: null,
            lastUpdatedByLastName: null
        };
        // reset tempComment
        student.tempComment = null;

        return this.postOneStudentComment(student, userComment);
    }

    deleteOneStudentComment(student: Student, comment: UserComment): Observable<StudentAddComment> {
        return this.http.delete<StudentAddComment>(
            '/courseStudent/deleteComment/' + student.id + '?commentId=' + comment.id,
            {}
        );
    }

    isGradeInList(list: GradeList[], grade: string) {
        return !!list.find((item: GradeList) => {
            return item.grade == grade;
        });
    }

    isGradingIP(inIPGrading: boolean, list: GradeList[], student: Student) {
        return (
            inIPGrading &&
            student?.gradeBanner &&
            (this.isGradeInList(list, student?.gradeBanner) || this.isGradeInList(list, student?.gradeOriginalIGrade))
        );
    }

    getPostStatus(transactionId: string, rosterId: number): Observable<GradePostStatus> {
        return this.http.get<GradePostStatus>('/api/courseRoster/status/' + transactionId + '/' + rosterId, {});
    }
}
